import { useState, useEffect, useRef } from 'react'
import { Settings, Menu, CheckSquare, Square, Printer, FileText } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import { lotesService } from '../../services/lotesService'
import { ordemEntradaService } from '../../services/ordemEntradaService'
import organizadoresService from '../../services/organizadoresService'
import fichaLeiloeiroService from '../../services/fichaLeiloeiroService'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useSidebar } from '../../components/layout/Layout'
import '../../components/catalogos/ficha-leiloeiro.css'

export default function FichaLeiloeiroList() {
  const [leiloes, setLeiloes] = useState([])
  const [selectedLeilao, setSelectedLeilao] = useState('')
  const [lotes, setLotes] = useState([])
  const [organizador, setOrganizador] = useState(null)
  const [selectedLotes, setSelectedLotes] = useState(new Set())
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [pdfModalType, setPdfModalType] = useState('selecionados') // 'selecionados' | 'branca'
  const iframeRef = useRef(null)
  const [impressos, setImpressos] = useState(new Set())
  const [filtro, setFiltro] = useState('todos') // 'todos' | 'nao_impressos' | 'impressos'
  const [config, setConfig] = useState({
    mostrar_vendedor: true,
    mostrar_cidade: true,
    mostrar_ordem: true,
    mostrar_lote: true,
    mostrar_qtd: true,
    mostrar_tipo: true,
    mostrar_raca: true,
    mostrar_curral: true,
    mostrar_sexo: true,
    mostrar_idade: true,
    mostrar_peso: true,
    mostrar_observacoes: true,
    fichas_por_folha: 1,
    mostrar_logo: true
  })
  const [loading, setLoading] = useState(true)
  const { organizadorAtivo } = useOrganizador()
  const { setSidebarOpen } = useSidebar()

  const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '')

  useEffect(() => {
    if (organizadorAtivo?.id) {
      loadLeiloes()
      loadConfig()
    }
  }, [organizadorAtivo])

  useEffect(() => {
    if (selectedLeilao && organizadorAtivo?.id) {
      loadLotes()
    }
  }, [selectedLeilao, organizadorAtivo])

  const loadLeiloes = async () => {
    try {
      const response = await leiloesService.getByOrganizador(organizadorAtivo.id, { per_page: 100 })
      const leiloesData = response || []
      setLeiloes(leiloesData)
      if (leiloesData.length > 0) {
        setSelectedLeilao((leiloesData.find(l => l.status === 'on') || leiloesData[0]).id.toString())
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error('Erro ao carregar leilões:', error)
      setLoading(false)
    }
  }

  // ── Seleção de lotes ──────────────────────────────────────────────────────
  // Helper: pega o ID real do lote (lote_id quando vem da ordem de entrada, senão id)
  const getLoteRealId = (lote) => lote.lote_id || lote.id

  const toggleLote = (lote) => {
    const realId = getLoteRealId(lote)
    setSelectedLotes(prev => {
      const next = new Set(prev)
      if (next.has(realId)) next.delete(realId)
      else next.add(realId)
      return next
    })
  }

  const selecionarTodos = () => setSelectedLotes(new Set(lotesFiltrados.map(l => getLoteRealId(l))))
  const selecionarNaoImpressos = () => setSelectedLotes(new Set(lotesFiltrados.filter(l => !l.ficha_impressa).map(l => getLoteRealId(l))))
  const desmarcarTodos = () => setSelectedLotes(new Set())

  const lotesFiltrados = lotes.filter(l => {
    if (filtro === 'nao_impressos') return !l.ficha_impressa
    if (filtro === 'impressos') return l.ficha_impressa
    return true
  })

  const handleImprimirSelecionados = async () => {
    if (selectedLotes.size === 0) return
    try {
      const { default: api } = await import('../../api/client')
      const ids = [...selectedLotes].join(',')
      const response = await api.get(
        `/organizadores/${organizadorAtivo.id}/catalogos/leiloes/${selectedLeilao}/ficha-leiloeiro?lote_ids=${ids}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([response.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfModalType('selecionados')
      setPdfUrl(URL.createObjectURL(blob))
      setShowPdfModal(true)
      // Atualiza lotes como impressos
      setLotes(prev => prev.map(l => selectedLotes.has(getLoteRealId(l)) ? { ...l, ficha_impressa: true } : l))
      // Marca no backend
      const idsMarcar = [...selectedLotes]
      setSelectedLotes(new Set())
      api.put(
        `/organizadores/${organizadorAtivo.id}/catalogos/leiloes/${selectedLeilao}/ficha-leiloeiro-marcar`,
        { lote_ids: idsMarcar, impressa: true }
      ).catch(() => alert('Não foi possível salvar as fichas como impressas. Tente novamente.'))
    } catch (err) {
      alert('Erro: ' + (err.response?.statusText || err.message))
    }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdfModal = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const handleBaixarFichaBranca = async () => {
    try {
      const { default: api } = await import('../../api/client')
      const response = await api.get(
        `/organizadores/${organizadorAtivo.id}/catalogos/ficha-leiloeiro-branca`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfModalType('branca')
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch (err) {
      alert('Erro ao gerar ficha em branco: ' + (err.response?.statusText || err.message))
    }
  }

  const loadConfig = async () => {
    try {
      const [orgResponse, configResponse] = await Promise.all([
        organizadoresService.getById(organizadorAtivo.id),
        fichaLeiloeiroService.getConfig(organizadorAtivo.id)
      ])
      setOrganizador(orgResponse)
      setConfig(configResponse)
    } catch (error) {
      console.error('Erro ao carregar configurações:', error)
    }
  }

  const loadLotes = async () => {
    try {
      setLoading(true)
      const [ordemData, lotesCompletos] = await Promise.all([
        ordemEntradaService.listarOrdem(selectedLeilao).catch(() => []),
        lotesService.listarLotesPorLeilao(organizadorAtivo.id, selectedLeilao)
      ])

      let lotesComDados
      if (ordemData && ordemData.length > 0) {
        const idsNaOrdem = new Set(ordemData.map(o => o.lote_id))
        const lotesOrdenados = ordemData
          .map(itemOrdem => ({
            ...lotesCompletos.find(l => l.id === itemOrdem.lote_id),
            ...itemOrdem,
          }))
          .filter(l => l.id)
          .sort((a, b) => a.posicao - b.posicao)

        const lotesFora = lotesCompletos
          .filter(l => !idsNaOrdem.has(l.id))
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0))

        const offset = lotesOrdenados.length
        lotesComDados = [
          ...lotesOrdenados,
          ...lotesFora.map((lote, i) => ({ ...lote, posicao: offset + i + 1 })),
        ]
      } else {
        lotesComDados = lotesCompletos
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0))
          .map((lote, index) => ({ ...lote, posicao: index + 1 }))
      }

      setLotes(lotesComDados)
      // Já deixa selecionados os lotes ainda não impressos
      setSelectedLotes(new Set(lotesComDados.filter(l => !l.ficha_impressa).map(getLoteRealId)))
    } catch (error) {
      console.error('Erro ao carregar lotes:', error)
      setLotes([])
    } finally {
      setLoading(false)
    }
  }

  // ── Fluxo cavalo: mesma lógica de reports/ficha_leiloeiro.py e
  // animal_spec_utils.py — QTD/TIPO/RAÇA/SEXO/IDADE vêm do(s) animal(is) do
  // lote (LoteAnimal), não do LoteCorte (fica vazio nesse fluxo). Gado
  // inalterado.
  const SEXO_LABEL = { M: 'Macho', F: 'Fêmea', C: 'Castrado' }
  const idadeLabel = (dataNascimento) => {
    if (!dataNascimento) return ''
    const nasc = new Date(dataNascimento)
    const hoje = new Date()
    let meses = (hoje.getFullYear() - nasc.getFullYear()) * 12 + (hoje.getMonth() - nasc.getMonth())
    if (hoje.getDate() < nasc.getDate()) meses -= 1
    meses = Math.max(0, meses)
    if (meses < 24) return `${meses} MES${meses !== 1 ? 'ES' : ''}`
    const anos = Math.floor(meses / 12)
    return `${anos} ANO${anos !== 1 ? 'S' : ''}`
  }
  // Uma linha de especificação por animal — espelha animal_spec_utils._linha_animal
  const linhaAnimal = (a) => {
    const pct = parseFloat(a.percentual_venda ?? 100)
    const sexo = SEXO_LABEL[a.sexo] || a.sexo || ''
    const raca = a.raca?.nome || ''
    const nasc = a.data_nascimento ? new Date(a.data_nascimento).toLocaleDateString('pt-BR') : ''
    const cab = [`${pct.toFixed(2)}%`, sexo, raca, a.nome || ''].filter(Boolean).join(' ')
    const extra = [
      nasc ? `NASC ${nasc}` : '',
      a.registro || '',
      a.pelagem || '',
      a.localizacao ? `LOCAL: ${a.localizacao}` : '',
    ].filter(Boolean).join(' / ')
    let linha = (extra ? `${cab} (${extra})` : cab).toUpperCase()
    if (a.informacoes) linha += ` - OBS: ${a.informacoes.toUpperCase()}`
    return linha
  }

  // Constrói URL absoluta da logo do organizador
  const logoSrc = organizador?.logo_url
    ? organizador.logo_url.startsWith('http')
      ? organizador.logo_url
      : `${apiBase}/${organizador.logo_url.replace(/^\//, '')}`
    : null

  if (loading && !selectedLeilao) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="">
      <div className="w-full px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="no-print bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 md:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-xl text-white hover:bg-white/20 transition-all duration-200"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-white truncate">Ficha do Leiloeiro</h1>
            <div className="w-10"></div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-3xl font-bold text-white hidden md:block">Ficha do Leiloeiro</h1>
              <p className="text-blue-100 mt-1 text-sm sm:text-base hidden sm:block">
                Fichas individuais para anotações durante o leilão
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedLeilao}
                onChange={(e) => setSelectedLeilao(e.target.value)}
                className="bg-white border border-gray-200 text-blue-600 px-3 py-2 rounded-xl text-sm font-medium min-w-[200px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecione um leilão...</option>
                {leiloes.map(leilao => (
                  <option key={leilao.id} value={leilao.id}>
                    {leilao.nome}
                  </option>
                ))}
              </select>
              {/* Botão ficha em branco */}
              {organizadorAtivo?.id && (
                <button
                  onClick={handleBaixarFichaBranca}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm font-medium"
                  title="Baixar fichas em branco"
                >
                  <FileText size={16} />
                  Ficha em Branco
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Barra de seleção */}
        {selectedLeilao && lotes.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-wrap items-center gap-3">
            <select value={filtro} onChange={e => setFiltro(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="todos">Todos ({lotes.length})</option>
              <option value="nao_impressos">Não impressos ({lotes.filter(l => !l.ficha_impressa).length})</option>
              <option value="impressos">Impressos ({lotes.filter(l => l.ficha_impressa).length})</option>
            </select>
            <button onClick={selecionarTodos} className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Selecionar Todos</button>
            <button onClick={selecionarNaoImpressos} className="px-3 py-2 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors">Selecionar Não Impressos</button>
            <button onClick={desmarcarTodos} className="px-3 py-2 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">Desmarcar</button>
            {selectedLotes.size > 0 && (
              <button onClick={handleImprimirSelecionados}
                className="ml-auto flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
                <Printer size={15} /> Imprimir {selectedLotes.size} selecionado(s)
              </button>
            )}
          </div>
        )}

        {/* Preview das fichas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : lotes.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum lote encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                {selectedLeilao ? 'Este leilão não possui lotes cadastrados.' : 'Selecione um leilão para visualizar as fichas.'}
              </p>
            </div>
          ) : (
            lotesFiltrados.map((lote, index) => {
              const isNewPage = index > 0 && index % config.fichas_por_folha === 0
              const loteNum = String(lote.numero_lote || '')
              const odStr = String(lote.posicao || index + 1)
              const vendedorNome = (lote.vendedor?.nome || '-').toUpperCase()
              const fazendaNome = (lote.fazenda?.nome || '').toUpperCase()
              const cidade = (lote.vendedor?.cidade || '').toUpperCase()
              const uf = (lote.vendedor?.estado || '').toUpperCase()
              const cidadeStr = cidade && uf ? `${cidade} - ${uf}` : cidade || uf || ''
              const peso = parseFloat(lote.peso || 0)
              const pesoStr = peso > 0 ? `${peso.toFixed(0)}kg / ${(peso / 30).toFixed(1)}@` : '-'

              // Fluxo cavalo: LoteCorte.tipo_animal fica vazio (dados vivem em
              // lote.animais) — mesmo sinal usado no backend (eh_cavalo).
              const ehCavaloLote = !lote.tipo_animal
              const animaisLote = ehCavaloLote ? (lote.animais || []) : []
              const animalUnico = animaisLote.length === 1 ? animaisLote[0] : null
              const qtdVal = ehCavaloLote ? String(animaisLote.length || lote.qtd || '-') : String(lote.qtd || '-')
              const tipoVal = ehCavaloLote
                ? (animalUnico?.tipo_animal?.nome?.toUpperCase() || '-')
                : (lote.tipo_animal?.nome?.toUpperCase() || '-')
              const racaVal = ehCavaloLote
                ? (animalUnico?.raca?.nome?.toUpperCase() || '-')
                : (lote.raca?.nome?.toUpperCase() || '-')
              const sexoVal = ehCavaloLote
                ? (animalUnico ? (SEXO_LABEL[animalUnico.sexo] || animalUnico.sexo || '-').toUpperCase() : '-')
                : (lote.sexo === 'Macho' ? 'M' : lote.sexo === 'Fêmea' ? 'F' : lote.sexo?.toUpperCase() || '-')
              const idadeVal = ehCavaloLote
                ? (animalUnico?.data_nascimento ? idadeLabel(animalUnico.data_nascimento) : '-')
                : (lote.idade?.nome?.toUpperCase() || '-')
              const especificacao = ehCavaloLote ? animaisLote.map(linhaAnimal).join('\n') : ''

              return (
                <div key={lote.id} className={`ficha-lote ${isNewPage ? 'page-break' : ''}`} style={{ position: 'relative' }}>
                  {/* Checkbox de seleção + badge impresso */}
                  <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <button onClick={() => toggleLote(lote)} className="p-0.5 hover:bg-gray-100 rounded">
                      {selectedLotes.has(getLoteRealId(lote))
                        ? <CheckSquare size={20} className="text-blue-600" />
                        : <Square size={20} className="text-gray-400" />}
                    </button>
                    {lote.ficha_impressa && (
                      <span className="text-[9px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">IMPRESSO</span>
                    )}
                  </div>
                  {/* Logos da ficha */}
                  {config.mostrar_logo && (
                    <div className="ficha-logos">
                      <div>
                        {logoSrc && (
                          <img src={logoSrc} alt="Logo Cliente" className="logo-cliente" />
                        )}
                      </div>
                      <div className="lote-destaque">
                        <div className="lote-destaque-label">LOTE</div>
                        <div className="lote-destaque-numero">{loteNum}</div>
                      </div>
                      <div>
                        <img
                          src="/assets/rematei.png"
                          alt="Rematei"
                          className="logo-rematei"
                          onError={e => { e.target.style.display = 'none' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Cabeçalho da ficha */}
                  <div className="ficha-header">
                    <div className="vendedor-section">
                      {config.mostrar_vendedor && (
                        <div className="vendedor-info">
                          {vendedorNome}
                          {fazendaNome ? ` - ${fazendaNome}` : ''}
                        </div>
                      )}
                      {cidadeStr && (
                        <div className="cidade-info">{cidadeStr}</div>
                      )}
                    </div>
                    <div className="header-right">
                      {config.mostrar_ordem && (
                        <div className="ordem">
                          <div className="ordem-label">O.D</div>
                          <div>{odStr}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dados do lote */}
                  <div className="ficha-dados" style={{
                    gridTemplateColumns: `repeat(${[
                      config.mostrar_qtd,
                      config.mostrar_tipo,
                      config.mostrar_raca,
                      config.mostrar_curral,
                      config.mostrar_sexo,
                      config.mostrar_idade,
                      config.mostrar_peso && !ehCavaloLote
                    ].filter(Boolean).length}, 1fr)`
                  }}>
                    {config.mostrar_qtd && (
                      <div className="campo">
                        <div className="label">QTD</div>
                        <div className="valor">{qtdVal}</div>
                      </div>
                    )}
                    {config.mostrar_tipo && (
                      <div className="campo">
                        <div className="label">TIPO</div>
                        <div className="valor">{tipoVal}</div>
                      </div>
                    )}
                    {config.mostrar_raca && (
                      <div className="campo">
                        <div className="label">RAÇA</div>
                        <div className="valor">{racaVal}</div>
                      </div>
                    )}
                    {config.mostrar_curral && (
                      <div className="campo">
                        <div className="label">CURRAL</div>
                        <div className="valor">{lote.curral || '-'}</div>
                      </div>
                    )}
                    {config.mostrar_sexo && (
                      <div className="campo">
                        <div className="label">SEXO</div>
                        <div className="valor">{sexoVal}</div>
                      </div>
                    )}
                    {config.mostrar_idade && (
                      <div className="campo">
                        <div className="label">IDADE</div>
                        <div className="valor">{idadeVal}</div>
                      </div>
                    )}
                    {config.mostrar_peso && !ehCavaloLote && (
                      <div className="campo">
                        <div className="label">PESO</div>
                        <div className="valor">{pesoStr}</div>
                      </div>
                    )}
                  </div>

                  {/* Especificação dos animais (fluxo cavalo) — mesmo formato
                      dos contratos: 1 linha por animal com % de venda, sexo,
                      raça, nome, nascimento/registro/pelagem/local e obs. */}
                  {ehCavaloLote && especificacao && (
                    <div className="ficha-observacoes">
                      <div className="obs-label">ESPECIFICAÇÃO</div>
                      <div className="obs-content" style={{ whiteSpace: 'pre-line' }}>{especificacao}</div>
                    </div>
                  )}

                  {/* Observações */}
                  {config.mostrar_observacoes && (
                    <div className="ficha-observacoes">
                      <div className="obs-label">OBSERVAÇÕES</div>
                      <div className="obs-content">{(() => {
                        let obs = lote.observacoes || '';
                        const criasF = lote.qtd_crias_femeas || 0;
                        const criasM = lote.qtd_crias_machos || 0;
                        if (criasF > 0 || criasM > 0) {
                          const partes = [];
                          if (criasF > 0) partes.push(`F:${criasF}`);
                          if (criasM > 0) partes.push(`M:${criasM}`);
                          const criasTxt = `Crias: ${partes.join(' ')}`;
                          obs = obs ? `${criasTxt} | ${obs}` : criasTxt;
                        }
                        return obs;
                      })()}</div>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Modal PDF selecionados */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl" style={{ height: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Printer size={20} className="text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {pdfModalType === 'branca' ? 'Ficha em Branco' : 'Fichas Selecionadas'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {pdfModalType === 'branca' ? 'Modelo em branco para impressão' : 'Lotes marcados como impressos'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, `Fichas Leiloeiro - ${leiloes.find(l => String(l.id) === String(selectedLeilao))?.nome || selectedLeilao}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium">
                  Baixar PDF
                </button>
                <button onClick={printPdfModal}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium">
                  <Printer size={16} /> Imprimir
                </button>
                <button onClick={closePdfModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <span className="text-gray-500 text-lg">✕</span>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Fichas Leiloeiro"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
